import { handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import {
  Campaign,
  CampaignActionsEnum,
  CampaignFormValues,
  CampaignTemplate,
} from '@/client/campaigns';
import { HubspotProperty, HubspotValue } from '@/client/hubspot/types';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { CampaignLaunchContext } from '@/components/campaigns';
import { CampaignForm } from '@/components/campaigns/forms';
import { StateProvider } from '@/components/state-provider';
import {
  useCampaign,
  useCampaignAction,
  useUpdateCampaign,
} from '@/hooks/query';
import {
  useCampaignTemplate,
  useUpdateCampaignTemplate,
} from '@/hooks/query/campaign-templates.hook';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { FlexContainer } from '@/ui/styled-ui';
import {
  getCampaignSubmitValueByStep,
  getCampaignTemplateSubmitValueByStep,
  prepareRequestData,
  queryStateConverter,
  setCampaignInitialValues,
  setCampaignTemplateInitialValues,
} from '@/utils/helpers';
import { hubspotTrack } from '@/utils/hubspot';
import { processIncludeExcludeTargetEntities } from '@/utils/targets';
import { AxiosError } from 'axios';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

export const UpdateCampaignPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { id } = useParams();
  const { state, search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const isTemplateFromUrl = queryParams.get('isTemplate') === 'true';
  const isTemplate = !!state?.isTemplate || isTemplateFromUrl;
  const account = useAppSelector(selectCurrentAccount);
  const [currentCampaign, setCurrentCampaign] = useState<Campaign | null>(null);
  const {
    campaign,
    isLoading: isCampaignLoading,
    refetch: refetchCampaign,
  } = useCampaign(id, {
    enabled: !isTemplate,
  });

  const {
    campaignTemplate,
    isLoading: isCampaignTemplateLoading,
    refetch: refetchCampaignTemplate,
  } = useCampaignTemplate({
    campaignId: id,
    enabled: isTemplate,
  });

  const [activeStep, setActiveStep] = useState(state?.activeStep ?? 0);
  const [initialValues, setInitialValues] = useState<
    CampaignFormValues | undefined
  >(undefined);

  const updateCampaignValues = async (campaign: Campaign) => {
    try {
      const { includedEntities, excludedEntities } =
        processIncludeExcludeTargetEntities({
          target: campaign.targets,
          excludes: campaign.excludes,
          account: account as Account,
        });

      setInitialValues(
        isTemplate
          ? setCampaignTemplateInitialValues(campaign as CampaignTemplate)
          : setCampaignInitialValues(
              campaign,
              includedEntities,
              excludedEntities,
            ),
      );
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  useEffect(() => {
    if ((isTemplate && !campaignTemplate) || (!isTemplate && !campaign)) return;

    if (isTemplate && !isCampaignTemplateLoading) {
      setCurrentCampaign(campaignTemplate as Campaign);
    } else if (!isTemplate && !isCampaignLoading) {
      setCurrentCampaign(campaign as Campaign);
    }
  }, [campaign, campaignTemplate]);

  useEffect(() => {
    if (!currentCampaign) return;
    updateCampaignValues(currentCampaign);
  }, [currentCampaign]);

  useEffect(() => {
    hubspotTrack(HubspotProperty.PHISHING_CAMPAIGN_VIEW, HubspotValue.YES);
  }, []);

  const handleOnStepChange = (step: number) => {
    setActiveStep(step);
    if (isTemplate) {
      refetchCampaignTemplate();
    } else {
      refetchCampaign();
    }
  };

  return (
    <StateProvider
      state={(campaign || campaignTemplate) && initialValues}
      isLoading={
        isCampaignLoading || isCampaignTemplateLoading || !initialValues
      }
      loadingMessage={t('campaign.loading.data')}
      errorMessage={t('campaign.loading.data.failed')}
    >
      <UpdateCampaignWithState
        campaign={(campaign || campaignTemplate) as Campaign}
        initialValues={initialValues as CampaignFormValues}
        onStepChange={handleOnStepChange}
        activeStep={activeStep}
        isTemplate={isTemplate}
        updateCampaignValues={updateCampaignValues}
      />
    </StateProvider>
  );
};

export const UpdateCampaignWithState: React.FC<{
  campaign: Campaign;
  initialValues: CampaignFormValues;
  activeStep: number;
  onStepChange: (step: number) => void;
  isTemplate: boolean;
  updateCampaignValues: (campaign: Campaign) => Promise<void>;
}> = ({
  campaign,
  initialValues,
  onStepChange,
  activeStep,
  isTemplate,
  updateCampaignValues,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const account = useAppSelector(selectCurrentAccount);
  const { setLaunchedCampaignId, isConnected } = useContext(
    CampaignLaunchContext,
  );

  const updateCampaign = useUpdateCampaign();
  const updateCampaignTemplate = useUpdateCampaignTemplate();
  const campaignAction = useCampaignAction();

  const pathItems = [
    {
      label: account?.name,
      url: !account?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id as string)
        : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
      template: AppBreadCrumbTemplate,
    },
    {
      label: t('campaigns'),
      url: RedirectPaths[RedirectPathsEnum.CAMPAIGNS](),
      template: AppBreadCrumbTemplate,
    },
    {
      label: t('generic.edit.name', {
        name:
          isTemplate && campaign?.masterTemplate
            ? campaign.masterTemplate.translations?.find(
                (tr) => tr.language === campaign?.language && tr.name,
              )?.name ??
              campaign.masterTemplate.translations?.find(
                (tr) => tr.language === 'en_GB' && tr.name,
              )?.name ??
              campaign?.name
            : campaign?.name,
      }),
      url: RedirectPaths[RedirectPathsEnum.CAMPAIGNS_EDIT](
        campaign?.id as string,
      ),
      template: AppBreadCrumbTemplate,
    },
  ];

  const handleSave = async (data: CampaignFormValues) => {
    const payload = prepareRequestData(account?.id as string, data);

    const partialPayload = isTemplate
      ? getCampaignTemplateSubmitValueByStep(activeStep, payload)
      : getCampaignSubmitValueByStep(activeStep, payload, true);

    const service = isTemplate ? updateCampaignTemplate : updateCampaign;
    const response = await service.update({
      campaignId: campaign.id,
      updates: partialPayload,
    });

    await updateCampaignValues({
      ...(campaign || {}),
      ...(response || {}),
    });
  };

  const handleLaunchCampaign = async (data: CampaignFormValues) => {
    const payload = prepareRequestData(account?.id as string, data);
    const partialPayload = isTemplate
      ? getCampaignTemplateSubmitValueByStep(activeStep, payload)
      : getCampaignSubmitValueByStep(activeStep, payload, true);

    await updateCampaign.update({
      campaignId: campaign.id,
      updates: partialPayload,
    });

    await campaignAction.updateStatus({
      campaignId: campaign.id,
      action: CampaignActionsEnum.LAUNCH,
    });
  };

  const handleSubmit = async (data: CampaignFormValues) => {
    try {
      setLaunchedCampaignId(campaign.id);

      await new Promise((resolve) => {
        const intervalId = setInterval(() => {
          if (isConnected.current) {
            clearInterval(intervalId);
            resolve(true);
          }
        }, 100);
      });

      await handleLaunchCampaign(data);

      navigate(RedirectPaths[RedirectPathsEnum.CAMPAIGNS]());
    } catch (e) {
      setLaunchedCampaignId(undefined);
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  return (
    <FlexContainer
      direction="column"
      align="flex-start"
      justify="flex-start"
      minHeight="100%"
    >
      <AppBreadCrumb
        model={pathItems}
        data-testid="update-campaign-bread-crumb"
        className="flex-initial"
      />
      <CampaignForm
        initialValues={initialValues}
        title={t('campaign.update')}
        onSubmit={handleSubmit}
        onSave={handleSave}
        saveState={queryStateConverter(updateCampaign)}
        campaignId={campaign.id}
        onStepChange={onStepChange}
        activeStep={activeStep}
        isTemplate={isTemplate}
        campaign={campaign}
      />
    </FlexContainer>
  );
};
